/**
 * useDataExceptionReport - poll a Data Exceptions matrix report job.
 *
 * Mirrors useReportJob's submit/poll loop but targets the
 * feature-gated /admin/data-exceptions/report* routes via
 * dataExceptionsApi. Kept separate because useReportJob is
 * hard-wired to /admin/reports/jobs. Job state lives in memory (no ?job=
 * URL caching, so the builder's filter URL params are left untouched).
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  dataExceptionsApi,
  type ReportFilters,
  type ReportJob,
} from '../services/admin/dataExceptionsService'

const POLL_INTERVAL = 500

interface UseDataExceptionReportReturn {
  job: ReportJob | null
  isPolling: boolean
  submit: (filters: ReportFilters) => Promise<void>
  reset: () => void
}

export function useDataExceptionReport(): UseDataExceptionReportReturn {
  const [job, setJob] = useState<ReportJob | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setIsPolling(false)
  }, [])

  useEffect(() => stop, [stop])

  const submit = useCallback(
    async (filters: ReportFilters) => {
      stop()
      setIsPolling(true)
      setJob({
        job_id: '',
        status: 'running',
        progress_percentage: 0,
        progress_message: 'Submitting...',
        result_summary: null,
        result_data: null,
        created_at: null,
        completed_at: null,
        error_message: null,
      })
      try {
        const { job_id } = await dataExceptionsApi.submitReport(filters)
        intervalRef.current = setInterval(async () => {
          try {
            const status = await dataExceptionsApi.getReport(job_id)
            setJob(status)
            if (status.status === 'completed' || status.status === 'failed') {
              stop()
            }
          } catch {
            stop()
            setJob((prev) =>
              prev
                ? { ...prev, status: 'failed', error_message: 'Failed to check job status' }
                : prev,
            )
          }
        }, POLL_INTERVAL)
      } catch {
        stop()
        setJob((prev) =>
          prev ? { ...prev, status: 'failed', error_message: 'Failed to submit report' } : prev,
        )
      }
    },
    [stop],
  )

  const reset = useCallback(() => {
    stop()
    setJob(null)
  }, [stop])

  return { job, isPolling, submit, reset }
}

export default useDataExceptionReport
